import { fill, maxBy, uniq } from 'lodash';

export interface HighlightMark {
  type: HighlightMarkType;
  paragraph: number;
  start: number;
  end: number;
}

type SparseMarkTypeArray = (HighlightMarkType | undefined)[];

export const HIGHLIGHT_MARK = {
  user: 'user',
  recompose: 'recompose',
  positive: 'positive',
  warning: 'warning',
  error: 'error',
  accept: 'accept',
} as const;
export type HighlightMarkType = (typeof HIGHLIGHT_MARK)[keyof typeof HIGHLIGHT_MARK];

export const PERSISTENT_HIGHLIGHT_MARKS: HighlightMarkType[] = [
  HIGHLIGHT_MARK.user,
  HIGHLIGHT_MARK.recompose,
];

// Former is higher priority
export const HIGHLIGHT_MARKS_PRIORITY: HighlightMarkType[] = [
  HIGHLIGHT_MARK.error,
  HIGHLIGHT_MARK.warning,
  HIGHLIGHT_MARK.positive,
  HIGHLIGHT_MARK.user,
  HIGHLIGHT_MARK.recompose,
];

export const onlyPersistentHighlightMarks = (marks: HighlightMark[]) =>
  marks.filter((mark) => PERSISTENT_HIGHLIGHT_MARKS.includes(mark.type));

export const moreImportantHighlightMark = (
  type1?: HighlightMarkType,
  type2?: HighlightMarkType
) => {
  if (!type1) {
    return true;
  }
  if (!type2) {
    return false;
  }

  const index1 = HIGHLIGHT_MARKS_PRIORITY.indexOf(type1);
  const index2 = HIGHLIGHT_MARKS_PRIORITY.indexOf(type2);
  return index1 == null || index1 > index2;
};

const fillMarkedArrayFromMarks = (marks: HighlightMark[]): SparseMarkTypeArray => {
  if (!marks?.length) {
    return [];
  }
  const endMark = maxBy(marks, 'end');
  if (!endMark) {
    return [];
  }

  const length = endMark.end + 1;
  const result: SparseMarkTypeArray = fill(Array(length), undefined);

  marks.forEach((mark) => {
    let counter = mark.start;
    while (counter < mark.end) {
      if (!result[counter] || moreImportantHighlightMark(result[counter], mark.type)) {
        result[counter] = mark.type;
      }
      counter++;
    }
  });

  return result;
};

const filterByParagraph = (array: HighlightMark[], paragraphIndex: number): HighlightMark[] =>
  array.filter((item) => item?.paragraph === paragraphIndex);

const collectMarksFromMarkedArray = (
  markedArray: SparseMarkTypeArray,
  paragraphIndex: number
): HighlightMark[] => {
  const result: HighlightMark[] = [];

  markedArray.reduce(
    (currentMark, currentType, currentIndex, array) => {
      // Last array item
      if (currentIndex === array.length - 1 && currentMark?.type) {
        result.push({
          type: currentMark.type,
          paragraph: paragraphIndex,
          start: currentMark.start,
          end: currentIndex,
        });
      } else if (currentType !== currentMark.type) {
        if (currentMark.type) {
          result.push({
            type: currentMark.type,
            paragraph: paragraphIndex,
            start: currentMark.start,
            end: currentIndex,
          });
        }
        return {
          paragraph: paragraphIndex,
          type: currentType,
          start: currentIndex,
        };
      }

      return currentMark;
    },
    { type: markedArray[0], start: 0 }
  );

  return result;
};

export const normalizeMarkup = (inputMarkup: HighlightMark[]): HighlightMark[] => {
  const paragraphs = uniq(inputMarkup.map(({ paragraph }) => paragraph));

  const outputMarks: HighlightMark[] = [];
  paragraphs.forEach((currentParagraph) => {
    const paragraphMarks = filterByParagraph(inputMarkup, currentParagraph);

    const markedArray = fillMarkedArrayFromMarks(paragraphMarks);
    const collectedMarks = collectMarksFromMarkedArray(markedArray, currentParagraph);
    outputMarks.push(...collectedMarks);
  });

  return outputMarks;
};
